import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { MapPin, ChevronDown, ArrowRight, ArrowLeft, Briefcase, Building2, GraduationCap, Users, CalendarDays, Clock } from "lucide-react";
import { Reveal, SectionHead, PageHero, CTASection, usePageMeta, Eyebrow } from "../components/ui-bits";
import { IMG } from "../data/content";
import ApplicationForm from "../components/ApplicationForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const fmtPosted = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
};

export const MetaChip = ({ icon: Icon, children }) =>
  children ? (
    <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
      <Icon size={13} className="text-[#C5221F]" />{children}
    </span>
  ) : null;

const Careers = () => {
  usePageMeta(
    "Careers — Join G. R. Engineering Private Limited",
    "Current job openings at G. R. Engineering Private Limited — engineering, fabrication, welding technology, QA/QC and project management careers at our Mumbai head office and Tarapur works."
  );
  const [jobs, setJobs] = useState(null);

  useEffect(() => {
    axios.get(`${API}/jobs-public`).then((r) => setJobs(r.data)).catch(() => setJobs([]));
  }, []);

  return (
    <>
      <PageHero
        img={IMG("columns-crane")}
        eyebrow="Careers"
        title="Build what others can't"
        sub="Our people are our biggest assets. Join a team that fabricates India's largest columns, vessels and storage systems — and continually upgrades its skills."
      />
      <section className="bg-slate-50" data-testid="careers-section">
        <div className="container-gre py-20 lg:py-28">
          <SectionHead eyebrow="Current Openings" title="Open positions" sub="Select a position to view full details and submit your application with your Resume/CV." />
          <div className="mt-12 max-w-4xl space-y-4">
            {jobs === null ? (
              <p className="text-sm text-slate-400">Loading openings…</p>
            ) : jobs.length === 0 ? (
              <div className="bg-white border border-slate-200 p-10 text-center" data-testid="careers-empty">
                <Briefcase size={28} className="mx-auto text-slate-300" />
                <p className="mt-4 font-display text-lg font-bold text-slate-900">No open positions right now</p>
                <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">We're always interested in skilled engineers, fabricators and project professionals. Send us your details and we'll reach out when a suitable role opens.</p>
                <Link to="/contact?interest=Careers" data-testid="careers-general-apply-btn" className="group mt-6 inline-flex items-center gap-3 bg-[#C5221F] hover:bg-[#A81B18] text-white font-display font-semibold px-7 py-3.5 text-xs uppercase tracking-widest transition-colors duration-300">
                  Send Your Details <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            ) : (
              jobs.map((j, i) => (
                <Reveal key={j.id} delay={i * 0.05}>
                  <Link to={`/careers/${j.id}`} className="group block bg-white border border-slate-200 card-lift p-7" data-testid={`job-card-${j.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <h3 className="font-display text-lg font-bold text-slate-900 group-hover:text-[#C5221F] transition-colors duration-200">{j.title}</h3>
                        <div className="mt-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5">
                          <MetaChip icon={MapPin}>{j.location}</MetaChip>
                          <MetaChip icon={Building2}>{j.department}</MetaChip>
                          <MetaChip icon={Clock}>{j.type}</MetaChip>
                          <MetaChip icon={Briefcase}>{j.experience}</MetaChip>
                          <MetaChip icon={CalendarDays}>{j.posted_at ? `Posted ${fmtPosted(j.posted_at)}` : ""}</MetaChip>
                        </div>
                      </div>
                      {j.vacancies && <span className="font-mono-tech text-[10px] font-bold uppercase tracking-widest border border-slate-200 text-slate-500 px-3 py-1.5">{j.vacancies} {Number(j.vacancies) === 1 ? "vacancy" : "vacancies"}</span>}
                    </div>
                    {j.description && <p className="mt-4 text-sm leading-relaxed text-slate-600 line-clamp-2">{j.description}</p>}
                    <span className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#C5221F]">
                      View &amp; Apply <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </Link>
                </Reveal>
              ))
            )}
          </div>
        </div>
      </section>
      <CTASection />
    </>
  );
};

export const CareerDetail = () => {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [missing, setMissing] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);

  useEffect(() => {
    setJob(null);
    setMissing(false);
    axios.get(`${API}/jobs-public/${jobId}`).then((r) => setJob(r.data)).catch(() => setMissing(true));
  }, [jobId]);

  usePageMeta(
    job ? `${job.title} — Careers | G. R. Engineering Private Limited` : "Careers | G. R. Engineering",
    job ? `Apply for ${job.title}${job.location ? ` at ${job.location}` : ""} — G. R. Engineering Private Limited careers.` : ""
  );

  useEffect(() => {
    if (job && new URLSearchParams(window.location.search).get("apply") === "1") setApplyOpen(true);
  }, [job]);

  if (missing) {
    return (
      <section className="bg-slate-50">
        <div className="container-gre py-32 text-center">
          <p className="font-display text-3xl font-bold text-slate-900">Vacancy not available</p>
          <p className="mt-3 text-slate-600">This position may have been filled or withdrawn.</p>
          <Link to="/careers" className="mt-7 inline-flex items-center gap-3 bg-[#C5221F] hover:bg-[#A81B18] text-white font-display font-semibold px-7 py-3.5 text-xs uppercase tracking-widest transition-colors duration-300" data-testid="job-notfound-back">
            View All Careers <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    );
  }
  if (!job) return <div className="container-gre py-32 text-center text-slate-400">Loading…</div>;

  const facts = [
    [MapPin, "Location", job.location],
    [Building2, "Department", job.department],
    [Clock, "Employment Type", job.type],
    [Briefcase, "Experience", job.experience],
    [GraduationCap, "Qualification", job.qualification],
    [Users, "Vacancies", job.vacancies],
    [CalendarDays, "Posted", fmtPosted(job.posted_at)],
    [CalendarDays, "Closing Date", job.closing_date],
  ].filter(([, , v]) => v);

  return (
    <>
      <section className="relative bg-[#0A1128] overflow-hidden" data-testid="job-detail-hero">
        <div className="absolute inset-0 blueprint-grid" />
        <div className="container-gre relative py-16 lg:py-24">
          <Reveal>
            <Link to="/careers" data-testid="back-to-careers-link" className="inline-flex items-center gap-2 font-mono-tech text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-400 hover:text-white transition-colors duration-200">
              <ArrowLeft size={14} /> All Careers
            </Link>
          </Reveal>
          <Reveal delay={0.08}><p className="mt-8"><Eyebrow light>Current Opening</Eyebrow></p></Reveal>
          <Reveal delay={0.14}>
            <h1 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight max-w-3xl">{job.title}</h1>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-300">
              {job.location && <span className="flex items-center gap-2"><MapPin size={15} className="text-[#E8574F]" />{job.location}</span>}
              {job.department && <span className="flex items-center gap-2"><Building2 size={15} className="text-[#E8574F]" />{job.department}</span>}
              {job.type && <span className="font-mono-tech text-[10px] font-bold uppercase tracking-widest border border-white/20 px-3 py-1.5">{job.type}</span>}
            </div>
          </Reveal>
          <Reveal delay={0.26}>
            <button onClick={() => setApplyOpen(true)} data-testid="job-apply-btn"
              className="group mt-9 inline-flex items-center gap-3 bg-[#C5221F] hover:bg-[#A81B18] text-white font-display font-semibold px-8 py-4 text-sm uppercase tracking-widest transition-colors duration-300">
              Apply for this Position <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#C5221F]" />
      </section>

      <section className="bg-white" data-testid="job-detail-body">
        <div className="container-gre py-16 lg:py-24 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-10">
            {job.description && (
              <div>
                <Eyebrow>Job Description</Eyebrow>
                <p className="mt-4 text-base leading-relaxed text-slate-700 whitespace-pre-line">{job.description}</p>
              </div>
            )}
            {!!job.responsibilities?.length && (
              <div>
                <Eyebrow>Roles &amp; Responsibilities</Eyebrow>
                <ul className="mt-4 space-y-3">
                  {job.responsibilities.map((r, i) => (
                    <li key={i} className="flex items-start gap-3 text-[15px] text-slate-700"><span className="mt-2 w-2 h-2 bg-[#C5221F] shrink-0" />{r}</li>
                  ))}
                </ul>
              </div>
            )}
            {!!job.requirements?.length && (
              <div>
                <Eyebrow>Requirements</Eyebrow>
                <ul className="mt-4 space-y-3">
                  {job.requirements.map((r, i) => (
                    <li key={i} className="flex items-start gap-3 text-[15px] text-slate-700"><span className="mt-2 w-2 h-2 bg-[#C5221F] shrink-0" />{r}</li>
                  ))}
                </ul>
              </div>
            )}
            {!!job.skills?.length && (
              <div>
                <Eyebrow>Skills Required</Eyebrow>
                <div className="mt-4 flex flex-wrap gap-2">
                  {job.skills.map((s) => (
                    <span key={s} className="font-mono-tech text-[11px] font-semibold uppercase tracking-widest border border-slate-200 bg-slate-50 text-slate-600 px-3.5 py-2">{s}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <aside>
            <div className="border border-slate-200 lg:sticky lg:top-28" data-testid="job-facts-card">
              <p className="px-5 py-3.5 bg-[#0A1128] font-mono-tech text-[10px] font-bold uppercase tracking-widest text-white">Position Details</p>
              <dl>
                {facts.map(([Icon, k, v], i) => (
                  <div key={k} className={`px-5 py-3.5 ${i > 0 ? "border-t border-slate-200" : ""}`}>
                    <dt className="flex items-center gap-2 font-mono-tech text-[10px] font-bold uppercase tracking-widest text-slate-400"><Icon size={12} className="text-[#C5221F]" />{k}</dt>
                    <dd className="mt-1 text-sm font-semibold text-slate-900">{v}</dd>
                  </div>
                ))}
              </dl>
              <div className="p-5 border-t border-slate-200">
                <button onClick={() => setApplyOpen(true)} data-testid="job-apply-sidebar-btn"
                  className="w-full bg-[#C5221F] hover:bg-[#A81B18] text-white font-display font-semibold py-3.5 text-xs uppercase tracking-widest transition-colors duration-300">
                  Apply Now
                </button>
                <p className="mt-3 text-[11px] text-slate-400 leading-relaxed">Your information is used only for recruitment purposes.</p>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <ApplicationForm job={job} open={applyOpen} onClose={() => setApplyOpen(false)} />
      <CTASection />
    </>
  );
};

export default Careers;
